import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Container, FormGroup, Input, Row } from "reactstrap";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import LaddersDataService from "../../../services/LaddersService";
import MsgDataService from "../../../services/MsgService";
import "./ParentMessage.scss";

// Import Advert component for Google ads
import Advert from "../../../components/Advert/Advert";

import { useDispatch, useSelector } from "react-redux";
import { getCommonMessage } from "../../../constants/CommonMessages";
import { messageNotificationAction } from "../../../reducers/MessageNotificationReducer";
const IMG_URL = process.env.REACT_APP_IMAGE_ASSET_URL;

const ParentMessage = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.LanguageReducer.current) || 'en';
  const isImpersonating = useSelector((state) => state.ImpersonationReducer?.isImpersonating);
  const [listLoading, setListLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [list, setList] = useState();
  const [filterList, setFilterList] = useState([]);
  const [searchMsg, setSearchMsg] = useState("");
  const [totalNotification, setTotalNotification] = useState(0);
  const [editChat, setEditChat] = useState(false);
  const [editUserInfo, setEditUserInfo] = useState({});
  const [userChat, setUserChat] = useState([]);
  const [allCount, setAllCount] = useState({});
  const chatRequestIdRef = useRef(0);
  const editUserInfoRef = useRef({});
  const scrollContainerRef = useRef(null);
  const chatLoadingRef = useRef(false);
  const editChatRef = useRef(false);

  const labels = {
    en: {
      messages: 'Messages',
      searchPlaceholder: 'Search contact and messages',
      youHave: 'You have',
      no: 'no',
      newMessageSingular: 'new message',
      newMessagePlural: 'new messages',
      emptyHeading: "Let's begin a conversation",
      emptyText: 'Pick a person from the menu on the left and start your conversation',
      download: 'Download Attachment',
    },
    es: {
      messages: 'Mensajes',
      searchPlaceholder: 'Buscar contactos y mensajes',
      youHave: 'Tienes',
      no: 'No tienes',
      newMessageSingular: 'mensaje nuevo',
      newMessagePlural: 'mensajes nuevos',
      emptyHeading: 'Comencemos una conversación',
      emptyText: 'Elige a una persona del menú de la izquierda y comienza tu conversación',
      download: 'Descargar archivo',
    },
  };
  const t = (key) => (labels[lang] || labels.en)[key];

  useEffect(() => {
    editUserInfoRef.current = editUserInfo;
  }, [editUserInfo]);

  useEffect(() => {
    chatLoadingRef.current = chatLoading;
  }, [chatLoading]);

  useEffect(() => {
    editChatRef.current = editChat;
  }, [editChat]);

  const getStudentWiseunreadCount = useCallback((id) => {
    const targetId = id || studentId;
    if (!targetId) return;
    MsgDataService.getUnreadCountForParent(targetId)
      .then((res) => {
        setAllCount(res?.data);
        setTotalNotification(res?.data?.total_notification);
        dispatch(messageNotificationAction(res?.data?.total_notification));
      })
      .catch(() => {});
  }, [dispatch, studentId]);

  const getReadCountbyDate = useCallback((id, student_id) => {
    MsgDataService.readCountDate({
      src_id: id,
      child_id: student_id,
    })
      .then((res) => {
        if (res?.status === 200) {
          dispatch(messageNotificationAction(res?.data?.count));
          getStudentWiseunreadCount(student_id);
        }
      })
      .catch(() => {});
  }, [dispatch, getStudentWiseunreadCount]);

  const openClickedContact = useCallback((item, { force = false } = {}) => {
    if (!item?.src_id || !studentId) return;
    if (
      !force &&
      editUserInfoRef.current?.src_id === item.src_id &&
      editChatRef.current &&
      !chatLoadingRef.current
    ) {
      return;
    }

    const requestId = ++chatRequestIdRef.current;
    setEditUserInfo(item);
    setEditChat(true);
    setUserChat([]);
    setChatLoading(true);

    if (!isImpersonating) {
      getReadCountbyDate(item.src_id, studentId);
    }

    MsgDataService.getMessageParentListPerUser(1, item.src_id, studentId)
      .then((data) => {
        if (requestId !== chatRequestIdRef.current) return;
        const msgObjArr = data?.data?.messages || [];
        setUserChat(msgObjArr);
      })
      .catch(() => {
        if (requestId !== chatRequestIdRef.current) return;
        setUserChat([]);
      })
      .finally(() => {
        if (requestId === chatRequestIdRef.current) {
          setChatLoading(false);
        }
      });
  }, [getReadCountbyDate, isImpersonating, studentId]);

  const getStudentList = useCallback(() => {
    // Only left-list loader — keep right empty state static (no page loader)
    setListLoading(true);
    LaddersDataService.getParentRoleStudentList()
      .then((res) => {
        if (res?.status === 200) {
          if (res?.data?.list?.length > 0) {
            const studentArr = res.data.list;
            setStudentList(studentArr);
            setStudentId(studentArr[0]?.id);
          } else {
            setStudentList([]);
            setList([]);
            setFilterList([]);
            setListLoading(false);
          }
        } else {
          setListLoading(false);
        }
      })
      .catch(() => {
        setList([]);
        setFilterList([]);
        setListLoading(false);
      });
  }, []);

  const getUserList = useCallback(() => {
    if (!studentId) return;
    setListLoading(true);
    MsgDataService.getParentMessageList(studentId)
      .then((res) => {
        if (res?.status === 200) {
          const messageList = res?.data?.data || [];
          setList(messageList);
          setFilterList(messageList);
        } else {
          setList([]);
          setFilterList([]);
        }
      })
      .catch(() => {
        setList([]);
        setFilterList([]);
      })
      .finally(() => {
        setListLoading(false);
      });
  }, [studentId]);

  useEffect(() => {
    getStudentList();
  }, [getStudentList]);

  const handleChangeStudent = useCallback((e) => {
    setSearchMsg("");
    setEditChat(false);
    setChatLoading(false);
    setListLoading(true);
    setUserChat([]);
    setEditUserInfo({});
    setList([]);
    setFilterList([]);
    setStudentId(e?.target?.value);
  }, []);

  useEffect(() => {
    if (studentId) {
      getUserList();
      getStudentWiseunreadCount(studentId);
    }
  }, [studentId, getUserList, getStudentWiseunreadCount]);

  // Re-fetch open conversation when language changes
  useEffect(() => {
    if (editChat && editUserInfoRef.current?.src_id && studentId) {
      openClickedContact(editUserInfoRef.current, { force: true });
    }
    // Only re-run on language change to avoid duplicate fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const handleChangeSearchMsg = useCallback((e) => {
    const value = e.target.value;
    setSearchMsg(value);
    const filterArr = (list || []).filter(
      (item) =>
        item.name
          .toLowerCase()
          .replace(/ +/g, "")
          .match(value.toLowerCase().replace(/ +/g, "")) ||
        item.src_role
          .toLowerCase()
          .replace(/ +/g, "")
          .match(value.toLowerCase().replace(/ +/g, ""))
    );
    setFilterList(filterArr);
  }, [list]);

  const getCountData = useCallback((item) => {
    if (!allCount) return "";
    const message = Object.keys(allCount).map((key) => {
      return allCount[key][item.src_id];
    });
    const data = message.filter((x) => x !== undefined);

    if (data && data[0] && data[0] !== 0) {
      return <div className="message-notification">{`${data[0]}`}</div>;
    }
    return "";
  }, [allCount]);

  useEffect(() => {
    if (!chatLoading && userChat.length > 0) {
      scrollContainerRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [chatLoading, userChat.length]);

  return (
    <>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          <div className="app-main__inner parentMessage">
            <div className="message-ad-header">
              <div className="header-content">
                <div className="ad-section-centered">
                  <Advert />
                </div>
              </div>
            </div>

            <div className="msg-main">
              <Container fluid>
                {studentList && studentList.length > 0 ? (
                  <Row className="studentWrap">
                    <Col lg={4} md={6}>
                      <FormGroup className="text-left studentSelect">
                        <Input
                          className="form-control"
                          type="select"
                          name="studentId"
                          value={studentId || ""}
                          onChange={handleChangeStudent}
                        >
                          {studentList.map((item, index) => {
                            return (
                              <option key={index} value={item.id}>
                                {item.name}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                ) : null}
                <div className="msgWrapper">
                  <Row>
                    <Col lg={4} md={6}>
                      <div className="messageSidebar">
                        <div className="sidebar-header">
                          <span className="title">{t('messages')}</span>
                          <div className="contactSearchWrap">
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="searchContactIcon"
                            />
                            <input
                              type="text"
                              className="form-control search-input contactSearch"
                              placeholder={t('searchPlaceholder')}
                              name="searchMsg"
                              value={searchMsg || ""}
                              autoComplete="off"
                              onChange={handleChangeSearchMsg}
                              disabled={listLoading}
                            />
                          </div>
                        </div>
                        <div className="list-wrap">
                          <p className="messageCount">
                            {lang === 'es' ? (
                              totalNotification && totalNotification > 0 ? (
                                <>
                                  {t('youHave')} {" "}
                                  <span className={"countText green"}>
                                    {totalNotification} {totalNotification === 1 ? t('newMessageSingular') : t('newMessagePlural')}
                                  </span>
                                </>
                              ) : (
                                <>
                                  {t('no')} {" "}
                                  <span className={'countText'}>
                                    {t('newMessagePlural')}
                                  </span>
                                </>
                              )
                            ) : (
                              <>
                                {t('youHave')} {" "}
                                <span
                                  className={`countText ${
                                    totalNotification && totalNotification > 0
                                      ? "green"
                                      : ""
                                  }`}
                                >
                                  {totalNotification && totalNotification > 0
                                    ? totalNotification
                                    : t('no')} {" "}
                                  {totalNotification === 1 ? t('newMessageSingular') : t('newMessagePlural')}
                                </span>
                              </>
                            )}
                          </p>
                          {listLoading ? (
                            <div className="list-loading-wrap">
                              <div className="chat-blue-loader" aria-label="Loading messages" />
                            </div>
                          ) : filterList && filterList.length > 0 ? (
                            filterList.map((item, index) => (
                              <div
                                className="list"
                                key={item?.src_id || index}
                                onClick={() => openClickedContact(item)}
                              >
                                <img
                                  className="profile-img"
                                  src={
                                    item?.profile_image ||
                                    IMG_URL + "placeholder.jpg"
                                  }
                                  alt=""
                                />
                                <div className="info">
                                  <span className="user">{item.name}</span>
                                  <span className="text">
                                    {item.src_role}
                                  </span>
                                </div>
                                <div>
                                  <div className="time">
                                    {getCountData(item)}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="list-empty-wrap">
                              {getCommonMessage('noDataFound', lang)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>

                    {/* Edit Screen */}
                    {editChat ? (
                        <Col lg={8} md={6}>
                          <div className="content">
                            <header>
                              <img
                                src={
                                  editUserInfo?.profile_image ||
                                  IMG_URL + "placeholder.jpg"
                                }
                                alt=""
                              />
                              <div className="info d-flex flex-column">
                                <span className="user">
                                  {editUserInfo?.name}
                                </span>
                              </div>
                            </header>
                            <div className="message-wrap">
                              {chatLoading ? (
                                <div className="chat-loading-wrap">
                                  <div className="chat-blue-loader" aria-label="Loading conversation" />
                                </div>
                              ) : (
                                userChat && userChat.length > 0
                                ? userChat.map((item, index) => {
                                    return index === userChat.length - 1 ? (
                                      <div
                                        className="userChatBlock"
                                        ref={scrollContainerRef}
                                        key={index}
                                      >
                                        <div
                                          className={`message-list ${
                                            item.src_id === studentId
                                              ? ""
                                              : "me"
                                          }`}
                                        >
                                          <div className="msg">
                                            <p>
                                              <div>
                                                {item.message_body}
                                              </div>
                                              <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: "blue",
                                                  textDecorationLine:
                                                    "Underline",
                                                  marginRight: "10px",
                                                }}
                                                href={item?.attachment_url}
                                              >
                                                {item?.attachment_url ? t('download') : " "}
                                              </a>
                                            </p>
                                          </div>
                                          <div className="time">
                                            {moment(item.date_sent).format(
                                              "Do MMM | hh:mm a"
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className="userChatBlock"
                                        key={index}
                                      >
                                        <div
                                          className={`message-list ${
                                            item.src_id === studentId
                                              ? ""
                                              : "me"
                                          }`}
                                        >
                                          <div className="msg">
                                            <p>
                                              <div>
                                                {item.message_body}
                                              </div>
                                              <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: "blue",
                                                  textDecorationLine:
                                                    "Underline",
                                                  marginRight: "10px",
                                                }}
                                                href={item?.attachment_url}
                                              >
                                                {item?.file_name}
                                              </a>
                                            </p>
                                          </div>
                                          <div className="time">
                                            {moment(item.date_sent).format(
                                              "Do MMM | hh:mm a"
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                : ""
                              )}
                            </div>
                          </div>
                        </Col>
                    ) : (
                      <Col lg={8} md={6}>
                        <div className="noOpenChatScreen">
                          <div className="nochatBlock">
                            <img
                              src={IMG_URL + "initChatImg.png"}
                              alt="initChatImg"
                              className="img-fluid"
                            />
                            <p>{t('emptyHeading')}</p>
                            <div className="chatText">
                              {t('emptyText')}
                            </div>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              </Container>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParentMessage;